import { PublicClientApplication } from '@azure/msal-browser'
import {
  AuthenticatedTemplate,
  MsalProvider,
  UnauthenticatedTemplate,
  useMsal,
} from '@azure/msal-react'
import ThemeProvider from '@mui/material/styles/ThemeProvider'
import { AnyAction } from '@reduxjs/toolkit'
import { Suspense, useEffect, useState } from 'react'
import { Provider, useDispatch, useSelector } from 'react-redux'
import { Route, Routes, useSearchParams } from 'react-router-dom'

import { AutoExtractNotificationListener } from './components/common/AutoExtractNotificationListener/AutoExtractNotificationListener'
import BulkExportNotificationListener from './components/common/BulkExportNotificationListener/BulkExportNotificationListener'
import { ProtectedRoute } from './components/common/ProtectedRoutes/ProtectedRoute'
import RegionalApprovalNotificationListener from './components/common/RegionalApprovalNotificationListener/RegionalApprovalNotificationListener'
import { ErrorBoundary } from './components/ErrorBoundary/ErrorBoundary'
import { Layout, Loading } from './components/Layout'
import { userRoles } from './constants/auth.constants'
import { useAppInsights } from './hooks/useAppInsights'
import { lennarTheme } from './lennarTheme'
import { familyTypes, PLAN_TYPE, SearchAndCompareEnum } from './lib/constants'
import { getEnvVar } from './lib/env'
import { ROUTES } from './lib/routes'
import {
  IdTokenClaims,
  loadUserInfoFromSession,
  setUserInfo,
} from './redux/slice/AuthSlice'
import { getDivisionAndRegionData } from './redux/slice/divisionsAndRegionsSlice'
import {
  getIsApprover,
  getRegionalPlanApprovals,
} from './redux/slice/plansSlice'
import { startConnection } from './redux/slice/signalRSlice'
import { RootState, store } from './redux/stores/store'
import { lazyWithRetry } from './utils/lazyWithRetry.util'

const Home = lazyWithRetry(() => import('./pages/Home'))
const Plans = lazyWithRetry(() => import('./pages/Plans'))
const MultiFamilyPlans = lazyWithRetry(() => import('./pages/MultiFamilyPlans'))
const FavoritesTab = lazyWithRetry(
  () => import('./components/Plans/Favorites/Favorites')
)
const SharedWithMe = lazyWithRetry(
  () => import('./components/Plans/SharedWithMe/SharedWithMe')
)
const CreatePlan = lazyWithRetry(() => import('./pages/CreatePlan/CreatePlan'))
const CopyShell = lazyWithRetry(() => import('./pages/CreatePlan/CopyShell'))
const CopyUnit = lazyWithRetry(() => import('./pages/CreatePlan/CopyUnit'))
const DetailedPlan = lazyWithRetry(
  () => import('./components/ViewOnly/DetailedPlan/DetailedPlan')
)
const PendingApprovals = lazyWithRetry(
  () => import('./components/Plans/PendingApprovals/PendingApprovals')
)
const RegionalApprovals = lazyWithRetry(
  () => import('./components/Plans/RegionalApprovals/RegionalApprovals')
)
const PendingPlans = lazyWithRetry(
  () => import('./components/Plans/PendingPlans/PendingPlans')
)
const AdminConsole = lazyWithRetry(() => import('./components/AdminConsole'))
const ViewOnly = lazyWithRetry(() => import('./components/ViewOnly/ViewOnly'))
const BulkExportTab = lazyWithRetry(
  () => import('./components/Plans/BulkExport/BulkExport')
)
const NoPage = lazyWithRetry(() => import('./pages/NoPage'))
const Logout = lazyWithRetry(() => import('./components/Auth/Logout'))

const AuthenticatedPages = () => {
  const dispatch = useDispatch()
  const { accounts } = useMsal()
  const account = accounts[0]
  const [searchParams] = useSearchParams()
  const planFamilyType = searchParams.get(PLAN_TYPE) || familyTypes.SINGLE
  const plansPageToReturn =
    planFamilyType === familyTypes.SINGLE ? <Plans /> : <MultiFamilyPlans />

  const ViewOnlyPage = <ViewOnly />

  const isViewOnlyEnabled = getEnvVar('REACT_APP_VIEW_ONLY_ENABLED') === 'true'

  const {
    isCorporateCreateUser,
    isCorporateViewUser,
    isDivisionCreateUser,
    isDivisionViewUser,
  } = useSelector((state: RootState) => state.Auth)
  const isViewOnlyUser = isCorporateViewUser || isDivisionViewUser

  // Set user info from MSAL account before rendering routes
  useEffect(() => {
    if (account && account.idTokenClaims) {
      dispatch(
        setUserInfo({
          account: {
            idTokenClaims: account.idTokenClaims as unknown as IdTokenClaims,
          },
          email: account.username,
        })
      )
    }
  }, [account, dispatch])

  useEffect(() => {
    if (account && account.username) {
      dispatch(getIsApprover() as unknown as AnyAction)
      // The signed-in user's own regional-plan approvals. Nothing else fetches
      // them, so without this the three-state Submit button never left "Submit
      // for approval" after a reload and the Drafts tab's approval column was
      // blank for every row (LTGC-5711).
      dispatch(
        getRegionalPlanApprovals({ view: 'mine' }) as unknown as AnyAction
      )
    }
  }, [account, dispatch])

  const isCurrentUserARegionalApprover = useSelector(
    (state: RootState) => state.plans?.isCurrentUserARegionalApprover
  )

  // LTGC-5711: the approver's queue drives the nav badge's pending count, so it
  // has to be loaded before the Regional Plan Approvals page is ever opened —
  // the page fetches it too, which simply refreshes the same slice.
  useEffect(() => {
    if (account?.username && isCurrentUserARegionalApprover) {
      dispatch(
        getRegionalPlanApprovals({ view: 'queue' }) as unknown as AnyAction
      )
    }
  }, [account, dispatch, isCurrentUserARegionalApprover])

  const renderPlansPage = (): React.ReactNode => {
    const shouldShowViewOnlyPage =
      isViewOnlyEnabled && (isViewOnlyUser || searchAndCompareMode)

    return shouldShowViewOnlyPage ? ViewOnlyPage : plansPageToReturn
  }

  const setInitialSearchAndCompareSetting = () => {
    if (isCorporateCreateUser || isDivisionCreateUser) {
      const localStorageViewOnly = localStorage.getItem(
        SearchAndCompareEnum.KEY
      )
      if (localStorageViewOnly === SearchAndCompareEnum.ENABLED) {
        return true
      }
    }

    return false
  }
  const [searchAndCompareMode, setSearchAndCompareMode] = useState<boolean>(
    setInitialSearchAndCompareSetting
  )

  useEffect(() => {
    if (isViewOnlyUser) return

    const updateSearchAndCompareMode = () => {
      if (isCorporateCreateUser || isDivisionCreateUser) {
        const localStorageValue = localStorage.getItem(SearchAndCompareEnum.KEY)
        setSearchAndCompareMode(
          localStorageValue === SearchAndCompareEnum.ENABLED
        )
      } else {
        setSearchAndCompareMode(false)
      }
    }

    updateSearchAndCompareMode()

    // listen for localStorage changes from other tabs
    const handleStorageChange = (event: StorageEvent) => {
      if (event.key === SearchAndCompareEnum.KEY) {
        updateSearchAndCompareMode()
      }
    }

    // listen for custom localStorage changes in same tab
    const handleCustomStorageChange = (event: CustomEvent) => {
      if (event.detail?.key === SearchAndCompareEnum.KEY) {
        updateSearchAndCompareMode()
      }
    }

    window.addEventListener(
      SearchAndCompareEnum.LOCAL_STORAGE_NAME,
      handleStorageChange
    )
    window.addEventListener(
      SearchAndCompareEnum.EVENT_NAME,
      handleCustomStorageChange as EventListener
    )

    return () => {
      window.removeEventListener(
        SearchAndCompareEnum.LOCAL_STORAGE_NAME,
        handleStorageChange
      )
      window.removeEventListener(
        SearchAndCompareEnum.EVENT_NAME,
        handleCustomStorageChange as EventListener
      )
    }
  }, [isViewOnlyEnabled, isCorporateCreateUser, isDivisionCreateUser])

  return (
    <AuthenticatedTemplate>
      <Suspense fallback={Loading()}>
        <Routes>
          <Route index element={<Home />} />
          <Route
            path="/create-plan"
            element={
              <ProtectedRoute
                allowedRoles={[
                  userRoles.CORPORATE_CREATE,
                  userRoles.DIVISION_CREATE,
                ]}
              >
                <CreatePlan />
              </ProtectedRoute>
            }
          />
          <Route
            path="/edit-plan/:planId/:planType"
            element={
              <ProtectedRoute
                allowedRoles={[
                  userRoles.CORPORATE_CREATE,
                  userRoles.DIVISION_CREATE,
                ]}
              >
                <CreatePlan />
              </ProtectedRoute>
            }
          />
          <Route
            path="/bulk-edit"
            element={
              <ProtectedRoute
                allowedRoles={[
                  userRoles.CORPORATE_CREATE,
                  userRoles.DIVISION_CREATE,
                ]}
              >
                <CreatePlan />
              </ProtectedRoute>
            }
          />
          <Route
            path="/copy-plan/:planId"
            element={
              <ProtectedRoute
                allowedRoles={[
                  userRoles.CORPORATE_CREATE,
                  userRoles.DIVISION_CREATE,
                ]}
              >
                <CreatePlan />
              </ProtectedRoute>
            }
          />
          <Route
            path="/copy-shell/:planId"
            element={
              <ProtectedRoute
                allowedRoles={[
                  userRoles.CORPORATE_CREATE,
                  userRoles.DIVISION_CREATE,
                ]}
              >
                <CopyShell />
              </ProtectedRoute>
            }
          />
          <Route
            path="/copy-unit/:planId"
            element={
              <ProtectedRoute
                allowedRoles={[
                  userRoles.CORPORATE_CREATE,
                  userRoles.DIVISION_CREATE,
                ]}
              >
                <CopyUnit />
              </ProtectedRoute>
            }
          />
          <Route
            path="/add-plan-elevation/:planId"
            element={
              <ProtectedRoute
                allowedRoles={[
                  userRoles.CORPORATE_CREATE,
                  userRoles.DIVISION_CREATE,
                ]}
              >
                <CreatePlan />
              </ProtectedRoute>
            }
          />
          <Route
            path="/add-shell-elevation/:planId"
            element={
              <ProtectedRoute
                allowedRoles={[
                  userRoles.CORPORATE_CREATE,
                  userRoles.DIVISION_CREATE,
                ]}
              >
                <CopyShell />
              </ProtectedRoute>
            }
          />
          <Route
            path="/add-unit-elevation/:planId"
            element={
              <ProtectedRoute
                allowedRoles={[
                  userRoles.CORPORATE_CREATE,
                  userRoles.DIVISION_CREATE,
                ]}
              >
                <CopyUnit />
              </ProtectedRoute>
            }
          />
          <Route
            path="/pre-populate-plan/:planId"
            element={
              <ProtectedRoute
                allowedRoles={[
                  userRoles.CORPORATE_CREATE,
                  userRoles.DIVISION_CREATE,
                ]}
              >
                <CreatePlan />
              </ProtectedRoute>
            }
          />
          <Route
            path="/pre-populate-shell/:planId"
            element={
              <ProtectedRoute
                allowedRoles={[
                  userRoles.CORPORATE_CREATE,
                  userRoles.DIVISION_CREATE,
                ]}
              >
                <CopyShell />
              </ProtectedRoute>
            }
          />
          <Route
            path="/pre-populate-unit/:planId"
            element={
              <ProtectedRoute
                allowedRoles={[
                  userRoles.CORPORATE_CREATE,
                  userRoles.DIVISION_CREATE,
                ]}
              >
                <CopyUnit />
              </ProtectedRoute>
            }
          />
          <Route
            path={ROUTES.PLANS}
            element={<ProtectedRoute>{renderPlansPage()}</ProtectedRoute>}
          />
          <Route
            path={ROUTES.SHELL_PLANS}
            element={<ProtectedRoute>{renderPlansPage()}</ProtectedRoute>}
          />
          <Route
            path={ROUTES.UNIT_PLANS}
            element={<ProtectedRoute>{renderPlansPage()}</ProtectedRoute>}
          />
          <Route
            path={ROUTES.FAVORITES_SHELLS}
            element={<ProtectedRoute>{renderPlansPage()}</ProtectedRoute>}
          />
          <Route
            path={ROUTES.FAVORITES_UNITS}
            element={<ProtectedRoute>{renderPlansPage()}</ProtectedRoute>}
          />
          <Route
            path={ROUTES.FAVORITES_SINGLE_FAMILY}
            element={<ProtectedRoute>{renderPlansPage()}</ProtectedRoute>}
          />
          <Route
            path={ROUTES.DRAFTS}
            element={
              <ProtectedRoute
                allowedRoles={[
                  userRoles.CORPORATE_CREATE,
                  userRoles.DIVISION_CREATE,
                ]}
              >
                {renderPlansPage()}
              </ProtectedRoute>
            }
          />
          {getEnvVar('REACT_APP_PENDING_PLANS_ENABLED') === 'true' && (
            <Route
              path={ROUTES.PENDING_PLANS}
              element={
                <ProtectedRoute
                  allowedRoles={[
                    userRoles.CORPORATE_CREATE,
                    userRoles.DIVISION_CREATE,
                  ]}
                >
                  <PendingPlans />
                </ProtectedRoute>
              }
            />
          )}
          <Route
            path={ROUTES.DIVISION}
            element={
              <ProtectedRoute allowedRoles={[userRoles.DIVISION_CREATE]}>
                {renderPlansPage()}
              </ProtectedRoute>
            }
          />
          <Route
            path={ROUTES.APPROVALS}
            element={
              <ProtectedRoute allowedRoles={[userRoles.CORPORATE_CREATE]}>
                <PendingApprovals />
              </ProtectedRoute>
            }
          />
          {/* LTGC-5711: regional plan approvers are a distinct set from the
              core approvers, so the queue is gated on the is-approver
              response rather than on a role. */}
          <Route
            path={ROUTES.REGIONAL_APPROVALS}
            element={
              <ProtectedRoute>
                <RegionalApprovals />
              </ProtectedRoute>
            }
          />
          <Route
            path={ROUTES.EXPORTS}
            element={
              <ProtectedRoute>
                <BulkExportTab />
              </ProtectedRoute>
            }
          />
          <Route
            path={ROUTES.FAVORITES}
            element={
              <ProtectedRoute
                allowedRoles={[
                  userRoles.CORPORATE_CREATE,
                  userRoles.DIVISION_CREATE,
                ]}
              >
                <FavoritesTab />
              </ProtectedRoute>
            }
          />
          <Route
            path={ROUTES.SHARED_WITH_ME}
            element={
              <ProtectedRoute
                allowedRoles={[
                  userRoles.CORPORATE_CREATE,
                  userRoles.DIVISION_CREATE,
                ]}
              >
                <SharedWithMe />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin-console"
            element={
              <ProtectedRoute allowedRoles={[userRoles.CORPORATE_CREATE]}>
                <AdminConsole />
              </ProtectedRoute>
            }
          />
          <Route
            path={`${ROUTES.DETAILED_PLAN}/:classType/:basePlanId/:plan/:planCategory/:groupBy`}
            element={
              <ProtectedRoute>
                <DetailedPlan />
              </ProtectedRoute>
            }
          />
          <Route path="*" Component={NoPage} />
        </Routes>
      </Suspense>
    </AuthenticatedTemplate>
  )
}
const UnAuthenticatedPages = () => {
  return (
    <UnauthenticatedTemplate>
      <Routes>
        <Route path="/logout" element={<Logout />} />
      </Routes>
    </UnauthenticatedTemplate>
  )
}

export const App: React.FC<{
  instance: PublicClientApplication
}> = ({ instance }) => {
  useAppInsights()
  const dispatch = useDispatch()
  useEffect(() => {
    dispatch(loadUserInfoFromSession())
    dispatch(getDivisionAndRegionData() as unknown as AnyAction)
    dispatch(startConnection())

    return () => {
      dispatch({ type: 'signalR/stopConnection' })
    }
  }, [dispatch])
  return (
    <Provider store={store}>
      <MsalProvider instance={instance}>
        <ThemeProvider theme={lennarTheme}>
          <ErrorBoundary>
            <Layout>
              <AutoExtractNotificationListener />
              <BulkExportNotificationListener />
              <RegionalApprovalNotificationListener />
              <AuthenticatedPages />
              <UnAuthenticatedPages />
            </Layout>
          </ErrorBoundary>
        </ThemeProvider>
      </MsalProvider>
    </Provider>
  )
}
